import * as THREE from 'three';
import { SheetState } from '../config/SheetConfig.js';
import { ANIMATION_DURATIONS } from '../config/SheetConfig.js';
import { calculateOverviewFrustum, calculateZoomFrustum } from './SheetUtils.js';

export class SheetAnimation {
    constructor(camera, gradientBackground) {
        this.camera = camera;
        this.gradientBackground = gradientBackground;
        
        // Brightness management
        this.brightnessTimer = null;
    }
    
    // Zoom to a specific image
    zoomToImage(imagePos, isSubsequentMovement, onComplete) {
        const { size, aspect } = calculateZoomFrustum();
        const halfSize = size / 2;
        
        // Animate camera frustum
        gsap.to(this.camera, {
            left: -halfSize * aspect,
            right: halfSize * aspect,
            top: halfSize,
            bottom: -halfSize,
            duration: isSubsequentMovement ? 0.3 : 1,
            ease: isSubsequentMovement ? "power2.inOut" : "power2.inOut",
            overwrite: 'auto',
            onUpdate: () => {
                this.camera.updateProjectionMatrix();
            }
        });
        
        // Animate camera position
        gsap.to(this.camera.position, {
            x: imagePos.x,
            y: imagePos.y,
            duration: isSubsequentMovement ? 0.5 : 1.5,
            ease: isSubsequentMovement ? "power2.inOut" : "power4.inOut",
            overwrite: 'auto',
            onComplete: onComplete
        });
        
        // Animate gradient background with parallax effect
        if (this.gradientBackground) {
            const parallaxFactor = 0.15; // Reduced for subtler effect
            gsap.to(this.gradientBackground.position, {
                x: imagePos.x * parallaxFactor,
                y: imagePos.y * parallaxFactor,
                duration: isSubsequentMovement ? 0.7 : 1.8,
                ease: isSubsequentMovement ? "power2.inOut" : "power3.inOut",
                overwrite: 'auto'
            });
        }
    }
    
    // Zoom out to show the entire sheet
    zoomOut(onComplete) {
        // Don't modify state parameter - ContactSheet manages its own state
        
        // Kill any existing camera position animations to prevent conflicts
        gsap.killTweensOf(this.camera.position);
        
        // Force immediate position reset if way off center (rapid nav cleanup)
        if (Math.abs(this.camera.position.x) > 2 || Math.abs(this.camera.position.y) > 2) {
            this.camera.position.x = 0;
            this.camera.position.y = 0;
        }
        
        gsap.to(this.camera.position, {
            x: 0,
            y: 0,
            duration: ANIMATION_DURATIONS.ZOOM_OUT_POSITION,
            ease: "power3.in",
            overwrite: 'auto'
        });
        
        const { size: frustumSize, aspect } = calculateOverviewFrustum();
        const halfHeight = frustumSize / 2;
        const halfWidth = frustumSize * aspect / 2;
        
        gsap.to(this.camera, {
            left: -halfWidth,
            right: halfWidth,
            top: halfHeight,
            bottom: -halfHeight,
            duration: ANIMATION_DURATIONS.ZOOM_OUT_FRUSTUM,
            delay: ANIMATION_DURATIONS.ZOOM_OUT_DELAY,
            ease: "power3.inOut",
            overwrite: 'auto',
            onUpdate: () => {
                this.camera.updateProjectionMatrix();
            },
            onComplete: onComplete
        });
        
        // Reset gradient background position
        if (this.gradientBackground) {
            gsap.to(this.gradientBackground.position, {
                x: 0,
                y: 0,
                duration: ANIMATION_DURATIONS.ZOOM_OUT_FRUSTUM * 1.2,
                delay: ANIMATION_DURATIONS.ZOOM_OUT_DELAY * 0.5,
                ease: "power2.out",
                overwrite: 'auto'
            });
        }
    }
    
    // Move to an adjacent image
    moveToAdjacentImage(imagePos, onComplete) {
        gsap.killTweensOf(this.camera.position);
        
        gsap.to(this.camera.position, {
            x: imagePos.x,
            y: imagePos.y,
            duration: ANIMATION_DURATIONS.SUBSEQUENT_MOVEMENT,
            ease: "power2.out",
            overwrite: 'auto',
            onComplete: onComplete
        });
        
        // Animate gradient background with parallax effect
        if (this.gradientBackground) {
            const parallaxFactor = 0.15;
            gsap.to(this.gradientBackground.position, {
                x: imagePos.x * parallaxFactor,
                y: imagePos.y * parallaxFactor,
                duration: ANIMATION_DURATIONS.SUBSEQUENT_MOVEMENT,
                ease: "power2.out",
                overwrite: 'auto'
            });
        }
    }
    
    // Set image brightness to highlight the active image
    // Pass meshCache for O(1) performance, falls back to scene.traverse if not provided
    setImageBrightness(scene, activeRow, activeCol, meshCache = null) {
        if (meshCache) {
            // O(1) optimized path using mesh cache
            for (let row = 0; row < 6; row++) { // GridLayout rows
                for (let col = 0; col < 6; col++) { // GridLayout columns
                    const meshKey = `${row},${col}`;
                    const mesh = meshCache.get(meshKey);
                    
                    if (mesh && mesh.material) {
                        // Kill existing animations to prevent conflicts
                        gsap.killTweensOf(mesh.material.color);
                        
                        const isActive = row === activeRow && col === activeCol;
                        if (isActive) {
                            gsap.to(mesh.material.color, {
                                r: 1.0,
                                g: 1.0,
                                b: 1.0,
                                duration: 0.35,
                                overwrite: 'auto'
                            });
                        } else {
                            gsap.to(mesh.material.color, {
                                r: 0.1,
                                g: 0.1,
                                b: 0.1,
                                duration: 0.5,
                                overwrite: 'auto'
                            });
                        }
                    }
                }
            }
        } else {
            // Fallback to scene traversal for backward compatibility
            scene.traverse(object => {
                if (object instanceof THREE.Mesh && 
                    object.userData && 
                    object.userData.isSheetImage) {
                    
                    if (object.material) {
                        // Kill existing animations to prevent conflicts
                        gsap.killTweensOf(object.material.color);
                        
                        const isActive = object.userData.row === activeRow && object.userData.col === activeCol;
                        if (isActive) {
                            gsap.to(object.material.color, {
                                r: 1.0,
                                g: 1.0,
                                b: 1.0,
                                duration: 0.35,
                                overwrite: 'auto'
                            });
                        } else {
                            gsap.to(object.material.color, {
                                r: 0.1,
                                g: 0.1,
                                b: 0.1,
                                duration: 0.5,
                                overwrite: 'auto'
                            });
                        }
                    }
                }
            });
        }
    }
    
    // Restore all images to full brightness
    resetImageBrightness(scene, meshCache = null) {
        if (meshCache) {
            // O(1) optimized path using mesh cache
            for (let row = 0; row < 6; row++) { // GridLayout rows
                for (let col = 0; col < 6; col++) { // GridLayout columns
                    const meshKey = `${row},${col}`;
                    const mesh = meshCache.get(meshKey);
                    
                    if (mesh && mesh.material) {
                        // Always restore brightness regardless of developing state
                        // when explicitly called (e.g., when zooming out to overview)
                        
                        // Kill existing animations to prevent conflicts
                        gsap.killTweensOf(mesh.material.color);
                        
                        gsap.to(mesh.material.color, {
                            r: 1.0,
                            g: 1.0,
                            b: 1.0,
                            duration: 0.35,
                            overwrite: 'auto',
                            onComplete: () => {
                                // Clear developing flag since we're force-completing the brightness
                                if (mesh.userData) {
                                    mesh.userData.isDeveloping = false;
                                }
                            }
                        });
                    }
                }
            }
        } else {
            // Fallback to scene traversal for backward compatibility
            scene.traverse(object => {
                if (object instanceof THREE.Mesh && 
                    object.userData && 
                    object.userData.isSheetImage) {
                    
                    if (object.material) {
                        // Always restore brightness regardless of developing state
                        // when explicitly called (e.g., when zooming out to overview)
                        
                        // Kill existing animations to prevent conflicts
                        gsap.killTweensOf(object.material.color);
                        
                        gsap.to(object.material.color, {
                            r: 1.0,
                            g: 1.0,
                            b: 1.0,
                            duration: 0.35,
                            overwrite: 'auto',
                            onComplete: () => {
                                // Clear developing flag since we're force-completing the brightness
                                if (object.userData) {
                                    object.userData.isDeveloping = false;
                                }
                            }
                        });
                    }
                }
            });
        }
    }
    
    // Schedule a brightness change with proper timing
    scheduleBrightnessChange(row, col, currentState, scene) {
        // Clear any existing brightness timer
        if (this.brightnessTimer) {
            clearTimeout(this.brightnessTimer);
            this.brightnessTimer = null;
        }
        
        // Set new brightness timer with state validation
        this.brightnessTimer = setTimeout(() => {
            // Only set brightness if still in animating state and not zooming out
            if (currentState === SheetState.ANIMATING && scene && this.brightnessTimer) {
                this.setImageBrightness(scene, row, col);
            }
            this.brightnessTimer = null;
        }, 250);
    }
    
    // Dispose of timers and clean up
    dispose() {
        if (this.brightnessTimer) {
            clearTimeout(this.brightnessTimer);
            this.brightnessTimer = null;
        }
    }
} 